// THE Control & Admin header search box (47231): one component for the Settings page and for every other Control & Admin page (ControlAdminSearch).
// Input (shared TextInput) + clear button + a results popover: group headers per tab/section, match highlight, hover + keyboard highlight (arrows / Enter / Esc), combobox + listbox ARIA.
// Only presentation: the caller owns the query, the index and what a pick does. groups = [{ key, label, entries:[{ id, displayPrimary, displayContext }] }].
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IcoLn } from './ActionIcons';
import { emoText } from './EmojiIcons';
import { TextInput } from '../components/inputs/Input';
import './headerSearch.css';

// entries of the shared shape ({ tabId, tabLabel, ...}) -> groups, in first-seen order
export function groupByTab(entries) {
  const m = new Map();
  for (const e of entries) { if (!m.has(e.tabId)) m.set(e.tabId, { key: e.tabId, label: e.tabLabel, entries: [] }); m.get(e.tabId).entries.push(e); }
  return [...m.values()];
}

function Mark({ text, q }) {
  const i = q ? String(text).toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
}

export default function HeaderSearch({ value, onChange, onClear, onFocus, onPick, groups, loading = false, placeholder, clearLabel, loadingText, emptyText, testid }) {
  const [open, setOpen] = useState(true);
  const [act, setAct] = useState(-1);
  const box = useRef(null);
  const q = value.trim();
  const flat = useMemo(() => groups.flatMap(g => g.entries), [groups]);
  useEffect(() => { setAct(-1); }, [value]);
  useEffect(() => {   // outside click closes the popover (the query stays)
    const off = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', off); return () => document.removeEventListener('mousedown', off);
  }, []);
  const show = !!q && open;
  const key = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (!show || !flat.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setAct(a => (a + 1) % flat.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAct(a => (a <= 0 ? flat.length - 1 : a - 1)); }
    else if (e.key === 'Enter' && act >= 0) { e.preventDefault(); onPick(flat[act]); }
  };
  let n = -1;
  return (
    <div className="hs" ref={box} data-testid={testid}>
      <span className="hs-in">
        <TextInput value={value} role="combobox" aria-expanded={show} aria-controls="hs-list" aria-autocomplete="list" aria-activedescendant={act >= 0 ? `hs-o-${act}` : undefined}
          onChange={e => { onChange(e.target.value); setOpen(true); }} onFocus={() => { if (onFocus) onFocus(); if (q) setOpen(true); }} onKeyDown={key} placeholder={placeholder} aria-label={placeholder} />
        {q && <button type="button" className="hs-clear" onClick={() => { onClear(); setOpen(true); }} title={clearLabel} aria-label={clearLabel}><IcoLn name="close" /></button>}
      </span>
      {show && <div className="hs-pop" role="listbox" id="hs-list">
        {loading ? <div className="hs-empty">{loadingText}</div>
          : !flat.length ? <div className="hs-empty">{emptyText}</div>
          : groups.map((g, gi) => <div key={g.key}>
              {gi > 0 && <div className="hs-div" />}
              <div className="hs-grp">{emoText(g.label)}</div>
              {g.entries.map(e => { n += 1; const i = n; return (
                <button key={e.id} type="button" role="option" id={`hs-o-${i}`} aria-selected={i === act} className={`hs-row${i === act ? ' act' : ''}`} onMouseEnter={() => setAct(i)} onClick={() => onPick(e)}>
                  <span className="hs-p"><Mark text={e.displayPrimary} q={q} /></span>
                  {e.displayContext && <span className="hs-c">{e.displayContext}</span>}
                </button>); })}
            </div>)}
      </div>}
    </div>
  );
}
